/* @flow */
import adminSchoolSisDashboardQuery from 'admin/queries/adminSchoolSisDashboardQuery.gql';
import BoxBody from 'components/BoxBody.jsx';
import Button from 'components/Button.jsx';
import Checkbox from 'components/Checkbox.jsx';
import ConfirmDialog from 'components/ConfirmDialog.jsx';
import Dialog from 'components/Dialog.jsx';
import ToastActions from 'actions/ToastActions';

import * as React from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';

import './styles/AdminSisSyncHoldViewResumeModal.scss';

type Props = {
  permanentlyIgnoreable: boolean,
  temporarilyIgnored: boolean,
  orgUuid: string,
  holdKey: string,
  syncMethod: string,
  onRequestHide: Function,
  resumeRostering: (
    orgUuid: string,
    skipPermanently: boolean,
    skipUntilSuccessful: boolean,
    holdKey: string
  ) => Promise<*>,
};

class AdminSisSyncHoldViewResumeModal extends React.Component<Props> {
  handleSubmit = () => {
    const { temporarilyIgnored, holdKey, orgUuid, resumeRostering } = this.props;
    const skipPermanently = !!(this.refs.checkbox && this.refs.checkbox.isChecked());
    const skipUntilSuccessful = !!temporarilyIgnored;
    return resumeRostering(orgUuid, skipPermanently, skipUntilSuccessful, holdKey)
      .then(result => {
        if (result.error) {
          const { fieldErrors } = result.error;
          const errorMessage =
            fieldErrors && fieldErrors.length
              ? result.error.fieldErrors.map(f => f.message).join(', ')
              : result.error.message;
          ToastActions.showErrorToast(errorMessage);
          console.debug(result.error);
          console.error('AdminSisSyncHoldViewResumeModal - could not resume');
        } else {
          ToastActions.showSuccessToast('Sync resumed');
        }
      })
      .catch(e => {
        ToastActions.showErrorToast('Oops, something went wrong resuming the sync');
        console.debug(e, e && e.stack);
        console.error('AdminSisSyncHoldViewResumeModal - hard fail - could not trigger resume');
      });
  };

  renderActionDialog(title: string, subtext: string) {
    const { permanentlyIgnoreable, onRequestHide } = this.props;
    return (
      <ConfirmDialog
        title={title}
        subtext={subtext}
        onRequestHide={onRequestHide}
        actionButtonText="Yes"
        cancelButtonText="Cancel"
        className="AdminSisSyncHoldViewResumeModal"
        preConfirmPromise={this.handleSubmit}>
        {permanentlyIgnoreable && (
          <div className="AdminSisSyncHoldViewResumeModal-checkbox">
            <Checkbox flush label="Don’t hold syncs like this again" name="skip" ref="checkbox" />
          </div>
        )}
      </ConfirmDialog>
    );
  }

  renderInformationalDialog(title: string, subtext: string) {
    const { onRequestHide } = this.props;
    return (
      <Dialog title={title} subtext={subtext} onRequestHide={onRequestHide} size="xsmall">
        <BoxBody>
          <Button level="secondary" onClick={onRequestHide} full>
            Got it
          </Button>
        </BoxBody>
      </Dialog>
    );
  }

  render() {
    const { temporarilyIgnored, syncMethod } = this.props;
    const title = temporarilyIgnored
      ? 'Ignore the hold and continue?'
      : 'Did you fix the data errors?';
    const subtext = temporarilyIgnored
      ? 'If you continue, we’ll resume the sync with the data changes we initially detected.'
      : 'We’ll start processing your data as soon as we receive it. If you’ve made changes, we’ll see them.';

    return temporarilyIgnored || syncMethod === 'Clever'
      ? this.renderActionDialog(title, subtext)
      : this.renderInformationalDialog(title, subtext);
  }
}

const withMutation = graphql(
  gql`
    mutation resumeRostering($input: ResumeRosteringInput!) {
      resumeRostering(input: $input) {
        error {
          message
          code
          fieldErrors {
            field
            message
          }
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      resumeRostering(
        orgUuid: string,
        skipPermanently: boolean,
        skipUntilSuccessful: boolean,
        holdKey: string
      ) {
        // TODO: remove the skip param when it's no longer required in apollo
        const input = {
          orgUuid,
          skip: skipPermanently,
          skipPermanently,
          skipUntilSuccessful,
          holdKey,
        };
        return mutate({
          variables: { input },
          update: (
            store,
            {
              data: {
                resumeRostering: { error },
              },
            }
          ) => {
            const currentData = store.readQuery({
              query: adminSchoolSisDashboardQuery,
              variables: { organizationUuid: [orgUuid] },
            });

            if (error) {
              return;
            }

            currentData.organizations[0].sisSyncOverview.syncStatus = 'processing';

            store.writeQuery({ query: adminSchoolSisDashboardQuery, data: currentData });
          },
        });
      },
    }),
  }
);

export { AdminSisSyncHoldViewResumeModal };
export default withMutation(AdminSisSyncHoldViewResumeModal);
